'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetMarketBackersInfinite } from '@morfi/backend-sdk-v2/client';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Suspense, useEffect, useState } from 'react';
import { InfiniteList } from '@/components/ui/infinite-list';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { BACKERS_PROJECTION } from '@/lib/sdk/projections';
import { logger } from '@/lib/telemetry/logger';
import { MarketTableSortControls } from '../market-table-sort-controls';
import type { BackerRewardsPool } from './backer-earnings';
import { MarketBackersEmpty } from './market-backers-empty';
import { MarketBackersError } from './market-backers-error';
import { MarketBackersSkeleton } from './market-backers-skeleton';
import { MarketBackersTable } from './market-backers-table';
import type { MarketBackerLeaderboardEntry, MarketBackerSortValue } from './types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface MarketBackersTabProps {
  marketPubkey: string;
  isActive: boolean;
  rewardPool: BackerRewardsPool | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isValidSort(v: string | null): v is MarketBackerSortValue {
  return v === 'amount' || v === 'shares' || v === 'recent' || v === 'earnings';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// `useSearchParams()` (read once below to seed the sort) requires a Suspense
// boundary above it so the route's static shell can render without opting the
// whole page into client-side rendering. The skeleton is the same pending UI
// the tab already shows while the backers query loads.
export function MarketBackersTab(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <MarketBackersSkeleton />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <Suspense fallback={t0}><MarketBackersTabInner {...props} /></Suspense>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function MarketBackersTabInner(t0) {
  const $ = _c(49);
  const {
    marketPubkey,
    isActive,
    rewardPool
  } = t0;
  const t = useTranslations("market.backers");
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    const raw = searchParams?.get("sort") ?? null;
    t1 = isValidSort(raw) ? raw : "earnings";
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const initialSort = t1;
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortBy, setSortBy] = useState(initialSort);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [...BACKERS_PROJECTION];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== marketPubkey || $[4] !== sortBy || $[5] !== sortOrder) {
    t3 = {
      pubkey: marketPubkey,
      sortBy,
      sortOrder,
      fields: t2
    };
    $[3] = marketPubkey;
    $[4] = sortBy;
    $[5] = sortOrder;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== isActive) {
    t4 = {
      enabled: isActive,
      resetPaginationOnRefetch: true
    };
    $[7] = isActive;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const query = useGetMarketBackersInfinite(t3, t4);
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = function handleSortChange(next) {
      setSortBy(next);
      setSortOrder("desc");
      if (typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        params.set("sort", next);
        const url = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
        window.history.replaceState(window.history.state, "", url);
      }
    };
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleSortChange = t5;
  const entries = useLiveListData(query.queryKey, query.rows as MarketBackerLeaderboardEntry[], query.entityQueryKey);
  const currentPool = query.isStale || query.isFetching || query.isError ? null : rewardPool;
  const earningsUnavailable = query.isError || rewardPool === null;
  let t6;
  let t7;
  if ($[10] !== query.error || $[11] !== query.isError) {
    t6 = () => {
      if (!query.isError) {
        return;
      }
      logger.warn("market_backers.fetch_failed", {
        errorClass: query.error instanceof Error ? query.error.name : "unknown"
      });
    };
    t7 = [query.isError, query.error];
    $[10] = query.error;
    $[11] = query.isError;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  if (query.isPending && entries.length === 0) {
    let t8;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <MarketBackersSkeleton />;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    return t8;
  }
  if (query.isError && entries.length === 0) {
    let t8;
    if ($[15] !== query) {
      t8 = <MarketBackersError onRetry={() => void query.refetch()} />;
      $[15] = query;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    return t8;
  }
  if (!query.isPending && !query.isError && entries.length === 0) {
    let t8;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <MarketBackersEmpty />;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    return t8;
  }
  let t8;
  if ($[18] !== t) {
    t8 = t("column_invested");
    $[18] = t;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t8) {
    t9 = {
      value: "amount",
      label: t8
    };
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t) {
    t10 = t("column_earnings");
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10) {
    t11 = {
      value: "earnings",
      label: t10
    };
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11 || $[27] !== t9) {
    t12 = [t9, t11];
    $[26] = t11;
    $[27] = t9;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== sortBy || $[30] !== sortOrder || $[31] !== t12) {
    t13 = <MarketTableSortControls className="xl:hidden" sortBy={sortBy} sortOrder={sortOrder} options={t12} onSortChange={handleSortChange} onSortOrderChange={setSortOrder} />;
    $[29] = sortBy;
    $[30] = sortOrder;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const t14 = `${marketPubkey}:${sortBy}:${sortOrder}`;
  const t15 = query.hasNextPage === true;
  const t16 = query.isFetchingNextPage === true;
  let t17;
  if ($[33] !== currentPool || $[34] !== earningsUnavailable || $[35] !== entries || $[36] !== sortBy || $[37] !== sortOrder) {
    t17 = <MarketBackersTable entries={entries} sortBy={sortBy} sortOrder={sortOrder} onSortOrderChange={setSortOrder} onSortChange={handleSortChange} rewardPool={currentPool} earningsUnavailable={earningsUnavailable} />;
    $[33] = currentPool;
    $[34] = earningsUnavailable;
    $[35] = entries;
    $[36] = sortBy;
    $[37] = sortOrder;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== query.fetchNextPage || $[40] !== query.isError || $[41] !== t14 || $[42] !== t15 || $[43] !== t16 || $[44] !== t17) {
    t18 = <InfiniteList key={t14} hasNextPage={t15} isFetchingNextPage={t16} fetchNextPage={query.fetchNextPage} isError={query.isError} maxHeight={560} scrollMode="page-below-lg" data-testid="market-backers-scroll">{t17}</InfiniteList>;
    $[39] = query.fetchNextPage;
    $[40] = query.isError;
    $[41] = t14;
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t13 || $[47] !== t18) {
    t19 = <div className="flex flex-col gap-2">{t13}{t18}</div>;
    $[46] = t13;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  return t19;
}